// ============================================================================
// CHANGO V5.0 — Billing plans page (D7 frontend)
// ============================================================================
// Mount: /app/billing/plans
//
// Role model:
//   - boss        : sees the full SKU grid + "开通 / 续费" button → POST
//                   /api/billing/checkout. Unpriced SKUs show a plain tag
//                   '敬请联系' per §11.2 (no fake pricing, no hidden price
//                   leaking into the DOM).
//   - finance     : sees prices + order list; cannot checkout.
//   - sales       : locked out at the router (RoleGate), not this page.
//
// This page does NOT call any sensitive endpoint (no cost/profit/margin). It
// strictly reads `amount_rmb` as a string from the server and displays it. It
// never multiplies qty × amount in JS to derive a total — the server returns
// the total_amount_rmb on checkout and we echo that back.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { trackEvent } from '../../lib/telemetry.js';
import RoleGate from '../../components/RoleGate.jsx';
import { brandForSku, sortByBrand } from '../../lib/skuBrandVersion.js';

const BILLING_CYCLE_LABEL = {
  monthly:  '按月',
  yearly:   '按年',
  one_time: '一次性',
  perpetual:'永久'
};

function formatRmbText(amount) {
  // Server returns amount_rmb as a decimal string, e.g. "1200.00" or null.
  if (amount == null) return null;
  const s = String(amount);
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return s;
  // Insert thousand separators before the decimal point.
  const [intPart, frac = '00'] = s.split('.');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `¥${withSep}.${(frac + '00').slice(0, 2)}`;
}

function SkuCard({ sku, canCheckout, onCheckout, busySku }) {
  const cycle = BILLING_CYCLE_LABEL[sku.billing_cycle] ?? sku.billing_cycle ?? '';
  const priceText = formatRmbText(sku.amount_rmb);
  const brandInfo = brandForSku(sku.sku_code);
  const seriesLabel = brandInfo.series === 'A' ? 'A 系（含工厂）'
    : brandInfo.series === 'B' ? 'B 系（仅外贸）'
    : '加购';
  const seriesBadge = brandInfo.series === 'A' ? 'bg-blue-50 text-blue-700 border-blue-200'
    : brandInfo.series === 'B' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <article className="card p-4 flex flex-col justify-between min-h-[14rem]">
      <div>
        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${seriesBadge}`}>
          {seriesLabel}
        </span>
        <h3 className="mt-2 text-base font-semibold text-slate-800">{brandInfo.shortName}</h3>
        <p className="mt-1 text-xs text-slate-500">{brandInfo.fullName}</p>
        <p className="mt-0.5 text-[11px] text-slate-400">代码 {sku.sku_code}</p>

        <div className="mt-3 flex items-baseline gap-2">
          {sku.available && priceText ? (
            <>
              <span className="text-2xl font-bold text-slate-900">{priceText}</span>
              {cycle && <span className="text-sm text-slate-500">/ {cycle}</span>}
            </>
          ) : (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              敬请联系
            </span>
          )}
        </div>

        <dl className="mt-3 space-y-1 text-xs text-slate-600">
          {sku.max_workspaces != null && (
            <div className="flex justify-between">
              <dt className="text-slate-500">工作区</dt>
              <dd>单工作区</dd>
            </div>
          )}
          {sku.max_users_per_ws != null && (
            <div className="flex justify-between">
              <dt className="text-slate-500">每空间最多成员</dt>
              <dd>{sku.max_users_per_ws}</dd>
            </div>
          )}
          {Array.isArray(sku.enabled_layers) && sku.enabled_layers.length > 0 && (
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">启用层</dt>
              <dd className="text-right">{sku.enabled_layers.join(', ')}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="mt-4">
        {canCheckout ? (
          sku.available ? (
            <button
              type="button"
              className="btn-primary w-full text-sm"
              disabled={busySku === sku.sku_code}
              onClick={() => onCheckout(sku)}
            >
              {busySku === sku.sku_code ? '创建订单中…' : '立即开通 / 续费'}
            </button>
          ) : (
            <a
              className="btn-ghost w-full text-sm text-center"
              href="mailto:contact@chango-tech.com?subject=企业定制咨询"
            >
              联系销售
            </a>
          )
        ) : (
          <p className="text-xs text-slate-400 text-center">仅老板角色可发起订单</p>
        )}
      </div>
    </article>
  );
}

function CheckoutModal({ open, sku, onClose, onConfirm, busy }) {
  const [qty, setQty] = useState(1);
  const [channel, setChannel] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setQty(1); setChannel(''); setNotes(''); setErr(null); }
  }, [open]);

  if (!open || !sku) return null;

  const submit = async () => {
    const q = Number.parseInt(qty, 10);
    if (!Number.isInteger(q) || q < 1 || q > 500) {
      setErr('数量必须在 1–500 之间');
      return;
    }
    try {
      await onConfirm({ sku_code: sku.sku_code, qty: q, channel: channel || null, notes: notes || null });
    } catch (e) {
      setErr(e?.message || '订单创建失败');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="card max-w-md w-full p-6">
        <h3 className="mb-1 text-lg font-semibold text-slate-800">确认订单</h3>
        <p className="mb-4 text-sm text-slate-500">{brandForSku(sku.sku_code).fullName} — 单价 {formatRmbText(sku.amount_rmb)}</p>

        <label className="block text-xs font-medium text-slate-600">数量</label>
        <input
          type="number"
          min={1}
          max={500}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="field"
        />

        <label className="mt-3 block text-xs font-medium text-slate-600">支付渠道（可选）</label>
        <select className="field" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">稍后选择</option>
          <option value="wechatpay">微信支付</option>
          <option value="alipay">支付宝</option>
          <option value="stripe">Stripe</option>
          <option value="manual">线下汇款</option>
        </select>

        <label className="mt-3 block text-xs font-medium text-slate-600">备注（可选）</label>
        <textarea
          className="field"
          rows={2}
          maxLength={500}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {err && <p className="err text-sm mt-3">{err}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>取消</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? '提交中…' : '确认下单'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderList({ orders, loading, err, onRefresh }) {
  return (
    <section className="card p-4 mt-6">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">订单记录</h3>
        <button type="button" className="btn-ghost text-sm" onClick={onRefresh}>刷新</button>
      </header>
      {err && <p className="err text-sm mb-3">{err}</p>}
      {loading ? (
        <p className="text-sm text-slate-500">加载中…</p>
      ) : orders.length === 0 ? (
        <p className="text-sm text-slate-500">尚无订单。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left py-2 pr-4">订单号</th>
                <th className="text-left py-2 pr-4">SKU</th>
                <th className="text-right py-2 pr-4">数量</th>
                <th className="text-right py-2 pr-4">金额</th>
                <th className="text-left py-2 pr-4">状态</th>
                <th className="text-left py-2">创建时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="py-2 pr-4 font-mono text-xs">{o.order_code}</td>
                  <td className="py-2 pr-4">{brandForSku(o.sku_code).shortName}</td>
                  <td className="py-2 pr-4 text-right">{o.qty}</td>
                  <td className="py-2 pr-4 text-right">{formatRmbText(o.total_amount_rmb)}</td>
                  <td className="py-2 pr-4">
                    <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {o.status}
                    </span>
                  </td>
                  <td className="py-2 text-xs text-slate-500">
                    {o.created_at ? new Date(o.created_at).toLocaleString('zh-CN', { hour12: false }) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ExtraSeatPanel({ sku, workspaceId, canCheckout, onPurchased }) {
  const [role, setRole] = useState('sales');
  const [paymentChannel, setPaymentChannel] = useState('wechat');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const priceText = formatRmbText(sku?.amount_rmb);

  if (!sku) return null;

  const submit = async () => {
    if (!workspaceId) {
      setMsg({ tone: 'error', text: '当前工作区信息缺失，请刷新后重试。' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post(`/api/workspace/${workspaceId}/seats/purchase`, {
        role,
        paymentChannel
      });
      setMsg({
        tone: 'success',
        text: `加购席位订单 ${res.order_code} 已创建 · 金额 ${formatRmbText(res.amount_rmb)}`
      });
      onPurchased?.();
    } catch (e) {
      setMsg({ tone: 'error', text: e instanceof HttpError ? e.message : '加购席位订单创建失败' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card mt-6 p-4">
      <header className="mb-3">
        <h3 className="text-base font-semibold text-slate-800">加购席位</h3>
        <p className="mt-1 text-sm text-slate-500">
          超出免费席位后按主套餐剩余有效期折算，到期时间与主套餐一致。基础价 {priceText || '敬请联系'} / 席 / 年。
        </p>
      </header>

      {msg && (
        <p className={[
          'mb-3 rounded px-3 py-2 text-sm',
          msg.tone === 'success'
            ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border border-rose-200 bg-rose-50 text-rose-800'
        ].join(' ')}>
          {msg.text}
        </p>
      )}

      {canCheckout ? (
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">席位角色</span>
            <select className="field mt-1" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="sales">业务员</option>
              <option value="finance">财务</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">支付渠道</span>
            <select className="field mt-1" value={paymentChannel} onChange={(e) => setPaymentChannel(e.target.value)}>
              <option value="wechat">微信</option>
              <option value="alipay">支付宝</option>
              <option value="bank">银行转账</option>
              <option value="other">其他</option>
            </select>
          </label>
          <button
            type="button"
            className="btn-primary self-end text-sm"
            disabled={busy}
            onClick={submit}
          >
            {busy ? '创建中…' : '创建加购订单'}
          </button>
        </div>
      ) : (
        <p className="text-sm text-slate-400">仅老板角色可发起加购席位订单。</p>
      )}
    </section>
  );
}

export default function BillingPlans() {
  const { user, workspace } = useAuth();
  const canCheckout = user?.role === 'boss';
  const canSeeOrders = user?.role === 'boss' || user?.role === 'finance';

  const [skus, setSkus] = useState([]);
  const [skusLoading, setSkusLoading] = useState(true);
  const [skusErr, setSkusErr] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersErr, setOrdersErr] = useState(null);

  const [modalSku, setModalSku] = useState(null);
  const [busySku, setBusySku] = useState(null);
  const [banner, setBanner] = useState(null);

  const loadSkus = useCallback(async () => {
    setSkusLoading(true); setSkusErr(null);
    try {
      const data = await api.get('/api/billing/skus');
      setSkus(Array.isArray(data?.skus) ? data.skus : []);
    } catch (e) {
      setSkusErr(e instanceof HttpError ? e.message : '加载失败');
    } finally {
      setSkusLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    if (!canSeeOrders) return;
    setOrdersLoading(true); setOrdersErr(null);
    try {
      const data = await api.get('/api/billing/orders?limit=50');
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
    } catch (e) {
      setOrdersErr(e instanceof HttpError ? e.message : '加载失败');
    } finally {
      setOrdersLoading(false);
    }
  }, [canSeeOrders]);

  useEffect(() => {
    loadSkus();
    loadOrders();
  }, [loadSkus, loadOrders]);

  const doCheckout = useCallback(async (payload) => {
    setBusySku(payload.sku_code);
    // D5.4 — announce the intent before the request so we also capture
    // sessions that abandon mid-flow (network failure, user cancels 3DS).
    try { trackEvent('billing.checkout_started', { sku_code: payload.sku_code, channel: payload.channel }); } catch {}
    try {
      const res = await api.post('/api/billing/checkout', payload);
      setBanner({
        tone: 'success',
        text: `订单 ${res.order_code} 已创建 · 金额 ${formatRmbText(res.total_amount_rmb)}（待支付）`
      });
      setModalSku(null);
      await loadOrders();
    } catch (e) {
      // Bubble up for the modal to display.
      throw e;
    } finally {
      setBusySku(null);
    }
  }, [loadOrders]);

  // §9.1 品牌矩阵分组：工厂版 → 外贸版 → 跨境版 → 企业版 → 加购
  const packageSkus = useMemo(
    () => skus.filter((s) => !s.is_addon && s.sku_code !== 'extra_seat'),
    [skus]
  );
  const extraSeatSku = useMemo(
    () => skus.find((s) => s.is_addon || s.sku_code === 'extra_seat') || null,
    [skus]
  );
  const sortedSkus = useMemo(
    () => sortByBrand(packageSkus, (s) => s.sku_code),
    [packageSkus]
  );

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">套餐与订单</h1>
          <p className="mt-1 text-sm text-slate-500">
            价格由长戈平台维护；报价截图或链接不作数，以此页为准。
          </p>
        </div>
        <Link to="/app/dashboard" className="btn-ghost text-sm">返回仪表盘</Link>
      </header>

      {banner && (
        <div className={[
          'mb-4 rounded px-3 py-2 text-sm',
          banner.tone === 'success'
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-rose-50 text-rose-800 border border-rose-200'
        ].join(' ')}>
          {banner.text}
          <button type="button" className="ml-3 text-xs underline" onClick={() => setBanner(null)}>关闭</button>
        </div>
      )}

      {skusErr && <p className="err mb-3">{skusErr}</p>}
      {skusLoading ? (
        <p className="text-sm text-slate-500">加载套餐…</p>
      ) : sortedSkus.length === 0 ? (
        <p className="text-sm text-slate-500">暂无可购买的套餐。</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sortedSkus.map((sku) => (
            <SkuCard
              key={sku.sku_code}
              sku={sku}
              canCheckout={canCheckout}
              onCheckout={(s) => setModalSku(s)}
              busySku={busySku}
            />
          ))}
        </div>
      )}

      <RoleGate allow={['boss', 'finance']}>
        <OrderList orders={orders} loading={ordersLoading} err={ordersErr} onRefresh={loadOrders} />
      </RoleGate>

      <ExtraSeatPanel
        sku={extraSeatSku}
        workspaceId={workspace?.id}
        canCheckout={canCheckout}
        onPurchased={loadOrders}
      />

      <CheckoutModal
        open={!!modalSku}
        sku={modalSku}
        busy={busySku != null}
        onClose={() => setModalSku(null)}
        onConfirm={doCheckout}
      />
    </div>
  );
}
